import React, { useEffect, useState } from 'react';
import { Loader2, CheckCircle2, Database, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Shows the 4 proxy sources JPRO consults during Step 1 (proxy_init).
 * Animates through them sequentially while the API call is in flight so
 * the user sees what's being looked up — not just an empty spinner.
 *
 * The animation is purely UI; we don't actually know which source is being
 * hit at any given millisecond. But each step name is grounded in real
 * data sources the backend uses (HUD PD&R, Census HVS, NCSHA Factbook).
 */

const SOURCES = [
    {
        key: 'vacancy',
        label: 'Local vacancy + rent trends',
        source: 'Census HVS / HUD PD&R',
        feeds: 'Market Stress Index',
    },
    {
        key: 'ami',
        label: 'Area Median Income & rent benchmarks',
        source: 'HUD PD&R',
        feeds: 'Rent-to-AMI Burden',
    },
    {
        key: 'oversight',
        label: 'State HFA monitoring intensity',
        source: 'NCSHA HFA Factbook',
        feeds: 'Oversight Credit',
    },
    {
        key: 'friction',
        label: 'Jurisdiction audit pressure',
        source: 'NCSHA HFA Factbook',
        feeds: 'Regulatory Friction',
    },
];

export default function BaselineFetchProgress({ active }) {
    const [activeIdx, setActiveIdx] = useState(0);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (!active) return;
        // Advance one source per ~1.6s. The real fetch typically completes within
        // 4–10s; if it finishes early the panel just unmounts. If it takes longer,
        // we hold on the last source until completion.
        const id = setInterval(() => {
            setActiveIdx(i => Math.min(i + 1, SOURCES.length - 1));
        }, 1600);
        return () => clearInterval(id);
    }, [active]);

    const completedCount = activeIdx;
    const stepLabel = SOURCES[Math.min(activeIdx, SOURCES.length - 1)]?.label;

    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
            {/* Collapsed header — always visible */}
            <button
                type="button"
                onClick={() => setExpanded(v => !v)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                <Database size={16} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 tracking-tight truncate">
                        Pulling baseline from public sources
                    </div>
                    {!expanded && (
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-0.5">
                            {completedCount} of {SOURCES.length} fetched · current: <span className="text-slate-700 dark:text-slate-200">{stepLabel}</span>
                        </div>
                    )}
                </div>
                <Loader2 size={14} className="animate-spin text-blue-500 flex-shrink-0" />
                {expanded
                    ? <ChevronUp size={14} className="text-slate-400 flex-shrink-0" />
                    : <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />}
            </button>

            {expanded && (
                <div className="px-4 pb-4 border-t border-slate-100 dark:border-slate-800">
                    <ul className="space-y-2 pt-3">
                        {SOURCES.map((s, i) => {
                            const status = i < activeIdx ? 'done' : i === activeIdx ? 'loading' : 'queued';
                            return (
                                <li key={s.key} className="flex items-start gap-2.5 py-1">
                                    <div className="flex-shrink-0 w-5 mt-0.5">
                                        {status === 'done' && <CheckCircle2 size={16} className="text-green-500" />}
                                        {status === 'loading' && <Loader2 size={16} className="animate-spin text-blue-500" />}
                                        {status === 'queued' && <span className="block w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-600 ml-1.5 mt-1.5" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className={`text-xs font-medium leading-tight ${
                                            status === 'queued'
                                                ? 'text-slate-400 dark:text-slate-500'
                                                : 'text-slate-800 dark:text-slate-100'
                                        }`}>{s.label}</div>
                                        <div className={`text-[11px] leading-tight mt-0.5 ${
                                            status === 'queued'
                                                ? 'text-slate-300 dark:text-slate-600'
                                                : 'text-slate-500 dark:text-slate-400'
                                        }`}>
                                            source: {s.source} · feeds <span className="font-medium">{s.feeds}</span>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>

                    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                        These four proxies set your starting score before any documents are uploaded. You can refine each one in the next step.
                    </div>
                </div>
            )}
        </div>
    );
}
